<template>
    <v-card class="mx-auto panel-prospectos" max-width="1400" elevation="2">
        <div v-if="loading" class="panel-loader">
            <div class="panel-loader__caja">
                <div class="panel-loader__spin"></div>
                <div class="panel-loader__titulo">Cargando prospectos</div>
                <div class="panel-loader__texto">Esto puede tardar un momento.</div>
            </div>
        </div>
        <v-card-title class="primary white--text py-3">
            <span class="headline">Panel de Prospectos</span>
            <v-spacer></v-spacer>

            <v-text-field v-model="search" append-icon="mdi-magnify" label="Buscar prospecto..." single-line
                hide-details dense outlined background-color="white" class="search-field mr-4"></v-text-field>

            <v-btn color="amber darken-2" dark @click="exportToExcel" :loading="exporting"
                :disabled="filteredProspects.length === 0">
                <v-icon left>mdi-microsoft-excel</v-icon>
                Exportar XLSX
            </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
            <!-- Filtros rápidos -->
            <v-row dense>
                <v-col cols="12" sm="6" md="3" lg="2">
                    <v-menu v-model="menuFechaInicio" :close-on-content-click="false" transition="scale-transition"
                        offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field v-model="filters.fechaInicio" label="Fecha inicial"
                                prepend-icon="mdi-calendar-start" readonly dense outlined hide-details clearable
                                v-bind="attrs" v-on="on"></v-text-field>
                        </template>
                        <v-date-picker v-model="filters.fechaInicio" :max="maxFecha" no-title scrollable
                            @input="menuFechaInicio = false"></v-date-picker>
                    </v-menu>
                </v-col>

                <!-- Fecha Final -->
                <v-col cols="12" sm="6" md="3" lg="2">
                    <v-menu v-model="menuFechaFin" :close-on-content-click="false" transition="scale-transition"
                        offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field v-model="filters.fechaFin" label="Fecha final" prepend-icon="mdi-calendar-end"
                                readonly dense outlined hide-details clearable v-bind="attrs" v-on="on"></v-text-field>
                        </template>
                        <v-date-picker v-model="filters.fechaFin" :min="filters.fechaInicio" :max="maxFecha" no-title
                            scrollable @input="menuFechaFin = false"></v-date-picker>
                    </v-menu>
                </v-col>
                <v-col cols="12" sm="6" md="3" lg="2">
                    <v-select v-model="filters.escuela" :items="escuelas" item-text="text" item-value="value"
                        label="Escuela" dense outlined hide-details @change="alCambiarEscuela"></v-select>
                </v-col>
                <v-col cols="12" sm="6" md="3" lg="2">
                    <v-select v-model="filters.ciudad" :items="ciudadesVisibles" label="Celular" clearable dense
                        outlined hide-details></v-select>
                </v-col>
                <v-col cols="12" sm="6" md="3" lg="2">
                    <v-select v-model="filters.etiqueta" :items="etiquetas" item-text="nombre" item-value="id"
                        label="Etiqueta" clearable dense outlined hide-details :menu-props="{ offsetY: true }">
                        <template v-slot:selection="{ item }">
                            <v-chip small :color="item.color" :text-color="item.texto || 'white'" class="mr-2">
                                {{ item.nombre }}
                            </v-chip>
                        </template>
                        <template v-slot:item="{ item }">
                            <v-chip small :color="item.color" :text-color="item.texto || 'white'">
                                {{ item.nombre }}
                            </v-chip>
                        </template>
                    </v-select>
                </v-col>

                <v-col cols="12" sm="6" md="3" lg="2">
                    <v-select v-model="filters.ventana" :items="ventanaOptions" label="Ventana de tiempo" clearable
                        dense outlined hide-details></v-select>
                </v-col>

                <v-col cols="12" sm="6" md="3" lg="2">
                    <v-select v-model="filters.plantel" :items="plantelesUnicos" label="Plantel" clearable dense
                        outlined hide-details></v-select>
                </v-col>

                <v-col cols="12" sm="6" md="3" lg="2">
                    <v-btn color="grey lighten-2" small @click="resetFilters" class="mt-6">
                        Limpiar filtros
                    </v-btn>
                </v-col>
                <v-col cols="12" sm="6" md="3" lg="2">
                    <v-btn color="primary" dark small :loading="loading" :disabled="loading" @click="getData" class="mt-6">
                        Actualizar
                    </v-btn>
                </v-col>
            </v-row>

            <!-- Tabla principal -->
            <v-data-table :headers="headers" :items="filteredProspects" :search="search" :items-per-page="15"
                :loading="loading" loading-text="Cargando prospectos..." class="elevation-1 mt-4" dense fixed-header height="calc(100vh - 320px)">
                <template v-slot:item.etiqueta_ia="{ item }">
                    <v-chip v-if="item.etiqueta_ia" small :color="getEtiquetaColor(item.etiqueta_ia)" :text-color="getEtiquetaTexto(item.etiqueta_ia)">
                        {{ getEtiquetaNombre(item.etiqueta_ia) }}
                    </v-chip>
                    <span v-else class="grey--text">Sin etiqueta</span>
                </template>

                <template v-slot:item.ciudad="{ item }">
                    <v-chip v-if="item.ciudad" small :color="colorCiudad(item.ciudad)" text-color="white">
                        {{ item.ciudad }}
                    </v-chip>
                </template>

                <template v-slot:item.tiemporestante="{ item }">
                    <v-chip :color="Number(item.tiemporestante) > 0 ? 'success' : 'grey darken-1'" small text-color="white">
                        {{ item.tiemporestante }} h
                    </v-chip>
                </template>

                <template v-slot:item.fecha_creacion="{ item }">
                    {{ formatDate(item.fecha_creacion) }}
                </template>

                <template v-slot:item.fecha_ultimo_mensaje="{ item }">
                    {{ formatDate(item.fecha_ultimo_mensaje) }}
                </template>

                <template v-slot:no-data>
                    <v-alert :value="true" color="warning" icon="mdi-alert">
                        No se encontraron prospectos con los filtros actuales
                    </v-alert>
                </template>
            </v-data-table>
        </v-card-text>

        <v-card-actions class="justify-center grey lighten-4 py-2">
            <span class="caption grey--text">
                Total prospectos: <strong>{{ filteredProspects.length }}</strong>
            </span>
        </v-card-actions>
    </v-card>
</template>

<script>
import * as XLSX from 'xlsx'

export default {
    name: 'ProspectosPanel',

    data: () => ({
        search: '',
        loading: false,
        exporting: false,

        prospects: [], // Aquí irá tu array de objetos

        menuFechaInicio: false,
        menuFechaFin: false,

        prospects: [],

        filters: {
            fechaInicio: null,    // formato 'YYYY-MM-DD'
            fechaFin: null,
            etiqueta: null,
            ventana: null,
            plantel: null,
            escuela: 1,
            ciudad: null
        },

        escuelas: [
            { text: 'INBI', value: 1 },
            { text: 'FAST', value: 2 }
        ],

        ventanaOptions: [
            { text: 'Ventana ABIERTA (≤ 24h)', value: 'abierta' },
            { text: 'Ventana CERRADA (> 24h)', value: 'cerrada' }
        ],

        headers: [
            { text: 'Folio', value: 'folio', width: '110px' },
            { text: 'Nombre', value: 'nombre_completo' },
            { text: 'Teléfono', value: 'telefono', width: '130px' },
            { text: 'Celular', value: 'ciudad', width: '100px' },
            { text: 'Etiqueta IA', value: 'etiqueta_ia', width: '190px', sortable: false },
            { text: 'Vendedora', value: 'vendedora' },
            { text: 'Plantel', value: 'plantel' },
            { text: 'Mensajes', value: 'mensajes', width: '110px', align: 'end' },
            { text: 'Ventana', value: 'tiemporestante', width: '110px', align: 'center' },
            { text: 'Últ. Mensaje', value: 'fecha_ultimo_mensaje', width: '140px' },
            { text: 'Creado', value: 'fecha_creacion', width: '140px' }
        ],

        etiquetas: [
            { id: 'primer_contacto', nombre: 'PRIMER CONTACTO', color: '#00BCD4', texto: 'white' },
            { id: 'spam', nombre: 'SPAM', color: '#000000', texto: 'white' },
            { id: 'cliente_no_contesta', nombre: 'CLIENTE NO CONTESTA', color: '#607D8B', texto: 'white' },
            { id: 'cliente_potencial', nombre: 'CLIENTE POTENCIAL', color: '#7E57C2', texto: 'white' },
            { id: 'no_interesado', nombre: 'NO INTERESADO', color: '#546E7A', texto: 'white' },
            { id: 'examen_enviado', nombre: 'EXAMEN ENVIADO', color: '#FBC02D', texto: 'black' },
            { id: 'examen_realizado', nombre: 'EXAMEN REALIZADO', color: '#F9A825', texto: 'black' },
            { id: 'cita_agendada', nombre: 'CITA AGENDADA', color: '#8E24AA', texto: 'white' },
            { id: 'pendiente_pago', nombre: 'PENDIENTE DE PAGO', color: '#E53935', texto: 'white' },
            { id: 'pago_parcial', nombre: 'PAGO PARCIAL', color: '#C0A000', texto: 'black' }
        ]
    }),

    computed: {
        maxFecha() {
            return new Date().toISOString().split('T')[0] // hoy
        },
        filteredProspects() {
            let result = [...this.prospects]

            // Filtro por etiqueta
            if (this.filters.etiqueta) {
                result = result.filter(p => p.etiqueta_ia === this.filters.etiqueta)
            }

            if (this.filters.ciudad) {
                result = result.filter(p => p.ciudad === this.filters.ciudad)
            }

            // Filtro por ventana de tiempo
            if (this.filters.ventana) {
                if (this.filters.ventana === 'abierta') {
                    result = result.filter(p => Number(p.tiemporestante) > 0)
                } else {
                    result = result.filter(p => Number(p.tiemporestante) <= 0)
                }
            }

            // Filtro por plantel
            if (this.filters.plantel) {
                result = result.filter(p => p.plantel === this.filters.plantel)
            }

            return result
        },

        ciudadesVisibles() {
            if (Number(this.filters.escuela) === 2) return ['FAST']
            return ['GDL', 'CDMX', 'MTY']
        },
        plantelesUnicos() {
            const planteles = new Set(this.prospects.map(p => p.plantel))
            return [...planteles].sort()
        }
    },
    created(){
        // this.getData()
    },
    methods: {
        getEtiquetaTexto(id) {
            const etiqueta = this.etiquetas.find(e => e.id === id)
            return etiqueta && etiqueta.texto ? etiqueta.texto : 'white'
        },

        colorCiudad(ciudad) {
            if (ciudad === 'GDL') return '#2E7D32'
            if (ciudad === 'CDMX') return '#EF6C00'
            if (ciudad === 'MTY') return '#1565C0'
            return '#6D4C41'
        },

        alCambiarEscuela() {
            this.filters.ciudad = null
        },

        getEtiquetaColor(id) {
            const etiqueta = this.etiquetas.find(e => e.id === id)
            return etiqueta ? etiqueta.color : 'grey'
        },

        getEtiquetaNombre(id) {
            const etiqueta = this.etiquetas.find(e => e.id === id)
            return etiqueta ? etiqueta.nombre : 'Sin etiqueta'
        },

        formatDate(dateStr) {
            if (!dateStr) return '-'
            const date = new Date(dateStr)
            return date.toLocaleString('es-MX', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            })
        },

        resetFilters() {
            this.filters.etiqueta = null
            this.filters.ventana = null
            this.filters.plantel = null
            this.filters.ciudad = null
            this.search = ''
        },

        exportToExcel() {
            if (this.filteredProspects.length === 0) return

            this.exporting = true

            try {
                // Preparar datos para exportar
                const data = this.filteredProspects.map(p => ({
                    Folio: p.folio,
                    Nombre: p.nombre_completo,
                    Teléfono: p.telefono,
                    Celular: p.ciudad || '',
                    'Etiqueta IA': this.getEtiquetaNombre(p.etiqueta_ia),
                    Vendedora: p.vendedora,
                    Plantel: p.plantel,
                    Mensajes: p.mensajes || 0,
                    'Ventana (horas)': p.tiemporestante,
                    'Último mensaje': this.formatDate(p.fecha_ultimo_mensaje),
                    Creado: this.formatDate(p.fecha_creacion)
                }))

                const worksheet = XLSX.utils.json_to_sheet(data)
                const workbook = XLSX.utils.book_new()

                XLSX.utils.book_append_sheet(workbook, worksheet, "Prospectos")

                // Autoajuste de columnas (opcional pero mejora mucho la experiencia)
                const colWidths = []
                const range = XLSX.utils.decode_range(worksheet['!ref'])
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    let maxWidth = 10
                    for (let R = range.s.r; R <= range.e.r; ++R) {
                        const cell = worksheet[XLSX.utils.encode_cell({ c: C, r: R })]
                        if (cell && cell.v) {
                            maxWidth = Math.max(maxWidth, String(cell.v).length + 2)
                        }
                    }
                    colWidths.push({ wch: Math.min(maxWidth, 60) })
                }
                worksheet['!cols'] = colWidths

                XLSX.writeFile(workbook, `Prospectos_${new Date().toISOString().slice(0, 10)}.xlsx`)
            } catch (error) {
                console.error('Error al exportar:', error)
                this.$toast.error('Error al generar el archivo Excel')
            } finally {
                this.exporting = false
            }
        },
        getData() {
            if (!this.filters.fechaInicio || !this.filters.fechaFin) return
            this.loading = true
            this.$http.post('obtener.prospectos.fecha', { escuela: this.filters.escuela || 1, fechaini: this.filters.fechaInicio, fechafin: this.filters.fechaFin }).then((response) => {
                this.prospects = Array.isArray(response.data) ? response.data : []
                this.loading = false
            }).catch(() => {
                this.prospects = []
                this.loading = false
            })
        }
    },

    // Ejemplo de cómo podrías cargar los datos
}
</script>

<style scoped>
.search-field {
    max-width: 320px;
}

.v-data-table>>>.v-data-table-header th {
    background-color: #f5f5f5 !important;
    font-weight: 600 !important;
}

.panel-loader {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 30000;
    background: rgba(15, 23, 42, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
}
.panel-loader__caja {
    background: #fff;
    border-radius: 16px;
    padding: 28px 36px;
    text-align: center;
    min-width: 280px;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.28);
}
.panel-loader__spin {
    width: 48px;
    height: 48px;
    margin: 0 auto 14px;
    border: 4px solid #e5e7eb;
    border-top-color: #1565c0;
    border-radius: 50%;
    animation: panelGiro 0.8s linear infinite;
}
.panel-loader__titulo {
    font-size: 18px;
    font-weight: 700;
    color: #111;
}
.panel-loader__texto {
    margin-top: 6px;
    font-size: 14px;
    color: #667;
}
@keyframes panelGiro {
    to { transform: rotate(360deg); }
}
</style>